"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusGUI = void 0;
const BaseComponent_1 = require("./Components/BaseComponent");
const InputComponents_1 = require("./Components/InputComponents");
const DisplayComponents_1 = require("./Components/DisplayComponents");
const ButtonComponent_1 = require("./Components/ButtonComponent");
const LayoutComponents_1 = require("./Components/LayoutComponents");
const AdvancedComponents_1 = require("./Components/AdvancedComponents");
/**
 * NexusGUI - 基于HTML+CSS的高性能UI系统
 *
 * 具有以下特性：
 * - 组件缓存与复用
 * - 性能监控
 * - 主题系统集成
 * - 类似Odin Inspector的简洁API
 *
 * @class NexusGUI
 * @since 1.0.0
 * @version 2.0.0 - 添加了性能优化功能
 *
 * @example
 * ```typescript
 * // 初始化GUI系统
 * NexusGUI.initialize();
 *
 * // 创建组件
 * const textField = NexusGUI.TextField('名称', '默认值');
 * const button = NexusGUI.Button('确定', () => {
 *     console.log('按钮被点击');
 * });
 *
 * // 获取性能指标
 * const metrics = NexusGUI.getPerformanceMetrics();
 * console.log('缓存命中率:', metrics.cacheHits / (metrics.cacheHits + metrics.cacheMisses));
 * ```
 */
class NexusGUI {
    /**
     * 私有构造函数（单例模式）
     * @private
     */
    constructor() {
        /** 组件缓存 */
        this.componentCache = new Map();
        /** 更新队列 */
        this.updateQueue = [];
        /** 是否正在更新 */
        this.isUpdating = false;
        /** 动画帧ID */
        this.rafId = null;
        /** 性能监控数据 */
        this.metrics = {
            renderCount: 0,
            cacheHits: 0,
            cacheMisses: 0,
            diffTime: 0,
            renderTime: 0,
            componentCount: 0,
            lastRenderTime: 0
        };
        /** 最大缓存大小 */
        this.maxCacheSize = 100;
        /** 缓存超时时间（5分钟） */
        this.cacheTimeout = 5 * 60 * 1000;
        this.setupPerformanceMonitoring();
        this.setupThemeListener();
    }
    /**
     * 获取单例实例
     * @returns NexusGUI实例
     * @since 2.0.0
     */
    static getInstance() {
        if (!NexusGUI.instance) {
            NexusGUI.instance = new NexusGUI();
        }
        return NexusGUI.instance;
    }
    /**
     * 设置性能监控
     * @private
     * @since 2.0.0
     */
    setupPerformanceMonitoring() {
        if (typeof window !== 'undefined') {
            // 每5秒清理一次缓存
            setInterval(() => {
                this.cleanupCache();
            }, 5000);
            // 监听内存压力
            if ('memory' in performance) {
                setInterval(() => {
                    const memInfo = performance.memory;
                    if (memInfo && memInfo.usedJSHeapSize > memInfo.totalJSHeapSize * 0.8) {
                        console.warn('内存压力过大，清理组件缓存');
                        this.forceCleanupCache();
                    }
                }, 10000);
            }
        }
    }
    /**
     * 设置主题监听器
     * @private
     * @since 2.0.0
     */
    setupThemeListener() {
        if (typeof window !== 'undefined') {
            window.addEventListener('nexus-theme-change', () => {
                this.invalidateAllComponents();
            });
        }
    }
    /**
     * 初始化GUI系统
     * @param container 可选的容器元素，如果不提供会自动创建
     * @since 1.0.0
     */
    static initialize(container) {
        const instance = NexusGUI.getInstance();
        BaseComponent_1.NexusGUIManager.initialize(container);
        instance.scheduleUpdate();
    }
    /**
     * 设置默认容器
     * @param container 容器元素
     * @since 1.0.0
     */
    static setContainer(container) {
        BaseComponent_1.NexusGUIManager.setDefaultContainer(container);
    }
    /**
     * 获取默认容器
     * @returns 默认容器元素
     * @since 1.0.0
     */
    static getContainer() {
        return BaseComponent_1.NexusGUIManager.getDefaultContainer();
    }
    // ========== 输入组件工厂方法 ==========
    /**
     * 创建文本输入框
     * @param label 标签文本
     * @param defaultValue 默认值
     * @param container 可选的容器元素
     * @returns TextFieldComponent实例
     * @since 1.0.0
     */
    static TextField(label, defaultValue, container) {
        const instance = NexusGUI.getInstance();
        const key = `textfield-${label}-${defaultValue || ''}`;
        return instance.getOrCreateComponent(key, () => new InputComponents_1.TextFieldComponent(label, defaultValue, container), { label, defaultValue, container });
    }
    /**
     * 创建切换开关
     * @param label 标签文本
     * @param defaultValue 默认值
     * @param container 可选的容器元素
     * @returns ToggleComponent实例
     * @since 1.0.0
     */
    static Toggle(label, defaultValue, container) {
        const instance = NexusGUI.getInstance();
        const key = `toggle-${label}-${defaultValue || false}`;
        return instance.getOrCreateComponent(key, () => new InputComponents_1.ToggleComponent(label, defaultValue, container), { label, defaultValue, container });
    }
    /**
     * 创建下拉选择框
     * @param label 标签文本
     * @param options 选项数组
     * @param defaultValue 默认选中的值
     * @param container 可选的容器元素
     * @returns DropdownComponent实例
     * @since 1.0.0
     */
    static Dropdown(label, options, defaultValue, container) {
        const instance = NexusGUI.getInstance();
        const key = `dropdown-${label}-${JSON.stringify(options)}-${defaultValue || ''}`;
        return instance.getOrCreateComponent(key, () => new InputComponents_1.DropdownComponent(label, options, defaultValue, container), { label, options, defaultValue, container });
    }
    /**
     * 创建按钮
     * @param text 按钮文本
     * @param onClick 点击回调函数
     * @param container 可选的容器元素
     * @returns ButtonComponent实例
     * @since 1.0.0
     */
    static Button(text, onClick, container) {
        const instance = NexusGUI.getInstance();
        const key = `button-${text}-${onClick ? onClick.toString() : ''}`;
        return instance.getOrCreateComponent(key, () => new ButtonComponent_1.ButtonComponent(text, onClick, container), { text, onClick, container });
    }
    // ========== 显示组件工厂方法 ==========
    /**
     * 创建标签
     * @param text 文本内容
     * @param container 可选的容器元素
     * @returns LabelComponent实例
     * @since 1.0.0
     */
    static Label(text, container) {
        const instance = NexusGUI.getInstance();
        const key = `label-${text}`;
        return instance.getOrCreateComponent(key, () => new DisplayComponents_1.LabelComponent(text, container), { text, container });
    }
    /**
     * 创建分隔符
     * @param container 可选的容器元素
     * @returns SeparatorComponent实例
     * @since 1.0.0
     */
    static Separator(container) {
        const instance = NexusGUI.getInstance();
        const key = `separator-${Date.now()}`;
        return instance.getOrCreateComponent(key, () => new DisplayComponents_1.SeparatorComponent(container), { container });
    }
    /**
     * 创建信息框
     * @param text 文本内容
     * @param type 信息类型
     * @param container 可选的容器元素
     * @returns InfoBoxComponent实例
     * @since 1.0.0
     */
    static InfoBox(text, type, container) {
        const instance = NexusGUI.getInstance();
        const key = `infobox-${text}-${type || 'info'}`;
        return instance.getOrCreateComponent(key, () => new DisplayComponents_1.InfoBoxComponent(text, type, container), { text, type, container });
    }
    // ========== 布局组件工厂方法 ==========
    /**
     * 创建分组容器
     * @param title 分组标题
     * @param container 可选的容器元素
     * @returns GroupComponent实例
     * @since 1.0.0
     */
    static Group(title, container) {
        const instance = NexusGUI.getInstance();
        const key = `group-${title}`;
        return instance.getOrCreateComponent(key, () => new LayoutComponents_1.GroupComponent(title, container), { title, container });
    }
    /**
     * 创建水平布局容器
     * @param gap 子元素间距，默认8px
     * @param container 可选的容器元素
     * @returns HorizontalComponent实例
     * @since 1.0.0
     */
    static Horizontal(gap, container) {
        const instance = NexusGUI.getInstance();
        const key = `horizontal-${gap || 8}`;
        return instance.getOrCreateComponent(key, () => new LayoutComponents_1.HorizontalComponent(gap, container), { gap, container });
    }
    // ========== 性能优化方法 ==========
    /**
     * 获取或创建组件（带缓存）
     * @param key 组件键
     * @param factory 组件工厂函数
     * @param props 组件属性
     * @returns 组件实例
     * @private
     * @since 2.0.0
     */
    getOrCreateComponent(key, factory, props) {
        const startTime = performance.now();
        const cached = this.componentCache.get(key);
        if (cached && !this.hasPropsChanged(cached.lastProps, props)) {
            // 缓存命中
            cached.lastUsed = Date.now();
            this.metrics.cacheHits++;
            return cached.component;
        }
        // 缓存未命中，创建新组件
        this.metrics.cacheMisses++;
        const component = factory();
        // 更新缓存
        this.componentCache.set(key, {
            component,
            lastProps: { ...props },
            lastUsed: Date.now(),
            dirty: false
        });
        // 清理超出限制的缓存
        if (this.componentCache.size > this.maxCacheSize) {
            this.cleanupCache();
        }
        const endTime = performance.now();
        this.metrics.renderTime += endTime - startTime;
        this.metrics.componentCount++;
        return component;
    }
    /**
     * 检查属性是否发生变化
     * @param oldProps 旧属性
     * @param newProps 新属性
     * @returns 是否发生变化
     * @private
     * @since 2.0.0
     */
    hasPropsChanged(oldProps, newProps) {
        if (!oldProps || !newProps)
            return true;
        const oldKeys = Object.keys(oldProps);
        const newKeys = Object.keys(newProps);
        if (oldKeys.length !== newKeys.length)
            return true;
        for (const key of oldKeys) {
            if (oldProps[key] !== newProps[key]) {
                return true;
            }
        }
        return false;
    }
    /**
     * 清理过期缓存
     * @private
     * @since 2.0.0
     */
    cleanupCache() {
        const now = Date.now();
        const expiredKeys = [];
        for (const [key, item] of this.componentCache) {
            if (now - item.lastUsed > this.cacheTimeout) {
                expiredKeys.push(key);
            }
        }
        expiredKeys.forEach(key => {
            this.componentCache.delete(key);
        });
        // 如果还是太多，删除最老的
        if (this.componentCache.size > this.maxCacheSize) {
            const sortedItems = Array.from(this.componentCache.entries())
                .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
            const toDelete = sortedItems.slice(0, sortedItems.length - this.maxCacheSize);
            toDelete.forEach(([key]) => {
                this.componentCache.delete(key);
            });
        }
    }
    /**
     * 强制清理所有缓存
     * @private
     * @since 2.0.0
     */
    forceCleanupCache() {
        this.componentCache.clear();
        console.log('已清理所有组件缓存');
    }
    /**
     * 使所有组件失效
     * @private
     * @since 2.0.0
     */
    invalidateAllComponents() {
        for (const item of this.componentCache.values()) {
            item.dirty = true;
        }
        this.scheduleUpdate();
    }
    /**
     * 安排更新
     * @private
     * @since 2.0.0
     */
    scheduleUpdate() {
        if (this.rafId !== null)
            return;
        this.rafId = requestAnimationFrame(() => {
            this.performUpdate();
            this.rafId = null;
        });
    }
    /**
     * 执行更新
     * @private
     * @since 2.0.0
     */
    performUpdate() {
        if (this.isUpdating)
            return;
        this.isUpdating = true;
        const startTime = performance.now();
        // 执行更新队列
        while (this.updateQueue.length > 0) {
            const update = this.updateQueue.shift();
            if (update) {
                update();
            }
        }
        // 更新脏组件
        this.updateDirtyComponents();
        const endTime = performance.now();
        this.metrics.renderTime += endTime - startTime;
        this.metrics.renderCount++;
        this.metrics.lastRenderTime = endTime;
        this.isUpdating = false;
    }
    /**
     * 更新脏组件
     * @private
     * @since 2.0.0
     */
    updateDirtyComponents() {
        for (const item of this.componentCache.values()) {
            if (item.dirty && item.component.update) {
                item.component.update();
                item.dirty = false;
            }
        }
    }
    /**
     * 将更新加入队列
     * @param update 更新函数
     * @since 2.0.0
     */
    queueUpdate(update) {
        this.updateQueue.push(update);
        this.scheduleUpdate();
    }
    // ========== 全局管理方法 ==========
    /**
     * 获取所有输入组件的值
     * @returns 包含所有输入组件值的对象
     * @since 1.0.0
     */
    static getAllValues() {
        const result = {};
        const components = BaseComponent_1.NexusGUIManager.getAllComponents();
        components.forEach(component => {
            if (component instanceof InputComponents_1.TextFieldComponent) {
                // 使用组件ID或标签作为键
                const key = component._label || component.id;
                result[key] = component.value;
            }
            else if (component instanceof InputComponents_1.ToggleComponent) {
                const key = component._label || component.id;
                result[key] = component.value;
            }
            else if (component instanceof InputComponents_1.DropdownComponent) {
                const key = component._label || component.id;
                result[key] = component.value;
            }
        });
        return result;
    }
    /**
     * 清理所有组件
     * @since 1.0.0
     */
    static clear() {
        const instance = NexusGUI.getInstance();
        instance.forceCleanupCache();
        BaseComponent_1.NexusGUIManager.clear();
    }
    /**
     * 获取所有组件
     * @returns 所有组件的数组
     * @since 1.0.0
     */
    static getAllComponents() {
        return BaseComponent_1.NexusGUIManager.getAllComponents();
    }
    /**
     * 根据ID获取组件
     * @param id 组件ID
     * @returns 组件实例或undefined
     * @since 1.0.0
     */
    static getComponent(id) {
        return BaseComponent_1.NexusGUIManager.getComponent(id);
    }
    /**
     * 获取性能监控数据
     * @returns 性能监控数据
     * @since 2.0.0
     */
    static getPerformanceMetrics() {
        return NexusGUI.getInstance().metrics;
    }
    /**
     * 重置性能监控数据
     * @since 2.0.0
     */
    static resetPerformanceMetrics() {
        const instance = NexusGUI.getInstance();
        instance.metrics = {
            renderCount: 0,
            cacheHits: 0,
            cacheMisses: 0,
            diffTime: 0,
            renderTime: 0,
            componentCount: 0,
            lastRenderTime: 0
        };
    }
    /**
     * 打印性能报告
     * @since 2.0.0
     */
    static printPerformanceReport() {
        const metrics = NexusGUI.getPerformanceMetrics();
        const cacheHitRate = metrics.cacheHits / (metrics.cacheHits + metrics.cacheMisses) * 100;
        console.group('🚀 NexusGUI 性能报告');
        console.log(`渲染次数: ${metrics.renderCount}`);
        console.log(`组件总数: ${metrics.componentCount}`);
        console.log(`缓存命中率: ${cacheHitRate.toFixed(2)}%`);
        console.log(`平均渲染时间: ${(metrics.renderTime / metrics.renderCount).toFixed(2)}ms`);
        console.log(`上次渲染时间: ${metrics.lastRenderTime.toFixed(2)}ms`);
        console.groupEnd();
    }
    // ========== 便捷方法 ==========
    /**
     * 创建一个快速表单
     * @param config 表单配置
     * @param container 可选的容器元素
     * @returns 包含所有组件的对象
     * @since 1.0.0
     */
    static QuickForm(config, container) {
        const result = {};
        // 创建分组（如果有标题）
        const group = config.title ? this.Group(config.title, container) : null;
        const targetContainer = group ? group.getContentContainer() : container;
        // 创建字段
        config.fields.forEach((field, index) => {
            const key = field.label.replace(/\s+/g, '').toLowerCase();
            switch (field.type) {
                case 'text':
                    result[key] = this.TextField(field.label, field.defaultValue, targetContainer);
                    break;
                case 'toggle':
                    result[key] = this.Toggle(field.label, field.defaultValue, targetContainer);
                    break;
                case 'dropdown':
                    result[key] = this.Dropdown(field.label, field.options || [], field.defaultValue, targetContainer);
                    break;
                case 'button':
                    result[key] = this.Button(field.label, field.onClick, targetContainer);
                    break;
            }
        });
        // 如果创建了分组，也返回分组引用
        if (group) {
            result._group = group;
        }
        return result;
    }
    /**
     * 创建表格组件
     *
     * @param config 表格配置
     * @param container 可选的容器元素
     * @returns 表格组件
     * @since 2.1.0
     */
    static Table(config, container) {
        const instance = NexusGUI.getInstance();
        const key = `table-${JSON.stringify(config)}`;
        return instance.getOrCreateComponent(key, () => new AdvancedComponents_1.TableComponent(config), { config, container });
    }
    /**
     * 创建树形视图组件
     *
     * @param config 树形视图配置
     * @param container 可选的容器元素
     * @returns 树形视图组件
     * @since 2.1.0
     */
    static TreeView(config, container) {
        const instance = NexusGUI.getInstance();
        const key = `treeview-${JSON.stringify(config)}`;
        return instance.getOrCreateComponent(key, () => new AdvancedComponents_1.TreeViewComponent(config), { config, container });
    }
    /**
     * 创建图表组件
     *
     * @param config 图表配置
     * @param container 可选的容器元素
     * @returns 图表组件
     * @since 2.1.0
     */
    static Chart(config, container) {
        const instance = NexusGUI.getInstance();
        const key = `chart-${JSON.stringify(config)}`;
        return instance.getOrCreateComponent(key, () => new AdvancedComponents_1.ChartComponent(config), { config, container });
    }
}
exports.NexusGUI = NexusGUI;
